import { FC, useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRedirectOnAuth } from '../../aws/AuthHooks';
import { AuthContext, AuthState } from '../../aws/AuthProvider';
import { ROUTE } from '../routes';
import { useStyles } from '../../theme/styles.helpers';
import { CalendarStyles } from './Calendar.Styles';
import { Box, Typography, Paper, Grid, Alert, Tabs, Tab } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventInput } from '@fullcalendar/core';
import { getSesamiBookings, getSesamiResources, patchSesamiAvailabilities } from '../../aws/RequestsApi';
import { CustomModal } from '../../components/modal/CustomModal';
import { BookingDetails } from '../../components/modals/booking-details/BookingDetails';
import AvailabilityModal from '../../components/modals/availability-modal/AvailabilityModal';
import { RefreshButton } from '../../components/button-components/refresh-button/RefreshButton';
import { SaveButton } from '../../components/button-components/save-button/SaveButton';


// utils
function weekdayToNumber(weekday: string) {
  const map: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  return map[weekday.toLowerCase()];
}

// Remove __typename properties from objects recursively
function removeTypename<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeTypename) as T;
  }
  
  if (typeof obj === 'object') {
    const cleaned = {} as Record<string, unknown>;
    for (const [key, value] of Object.entries(obj)) {
      if (key !== '__typename') {
        cleaned[key] = removeTypename(value);
      }
    }
    return cleaned as T;
  }
  
  return obj;
}

function numberToWeekday(n: number) {
  return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][n];
}

// Parse "YYYY-MM-DD" to a local Date (avoid timezone shift)
function parseISODateToLocal(isoDate: string) {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function formatLocalDateToISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function addDaysLocal(d: Date, days: number) {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c;
}

export function getWeekdayFromDate(dateString: string): string {
  const date = new Date(dateString);
  const weekdays = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];
  return weekdays[date.getDay()];
}

/**
 * Map Sesami availabilities into FullCalendar events.
 * - rangeStart / rangeEnd are inclusive ISO dates "YYYY-MM-DD" or Date objects.
 * - If rangeStart/rangeEnd are omitted, function falls back to producing
 *   day-of-week recurring events (but these cannot be excluded per-date).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSesamiToEvents(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availabilities: any[],
  rangeStart?: string | Date,
  rangeEnd?: string | Date
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const events: any[] = [];

  // collect override dates (date-specific availabilities present)
  const overrideDates = new Set<string>();
  for (const a of availabilities) {
    if (a.date) overrideDates.add(a.date);
  }

  // 1) map specific-date availabilities
  for (const a of availabilities) {
    if (!a.date) continue;
    const date = a.date; // "YYYY-MM-DD"

    if (!a.intervals || a.intervals.length === 0) {
      continue;
    }

    // normal date-specific intervals
    for (const i of a.intervals) {
      events.push({
        id: i.id,
        start: `${date}T${i.from}`,
        end: `${date}T${i.to}`,
        display: i.unavailable ? 'background' : 'block',
        color: i.unavailable ? '#ccc' : '#3788d8',
        title: `${i.from}-${i.to}`
      });
    }
  }

  // 2) map recurring weekday availabilities
  if (rangeStart && rangeEnd) {
    const startDate =
      typeof rangeStart === 'string' ? parseISODateToLocal(rangeStart) : new Date(rangeStart);
    const endDate =
      typeof rangeEnd === 'string' ? parseISODateToLocal(rangeEnd) : new Date(rangeEnd);

    for (let d = new Date(startDate); d <= endDate; d = addDaysLocal(d, 1)) {
      const iso = formatLocalDateToISO(d);

      if (overrideDates.has(iso)) {
        // skip recurring slots → day stays empty if override has no intervals
        continue;
      }

      const weekday = numberToWeekday(d.getDay());
      const weeklies = availabilities.filter(
        (a) => a.weekday && a.weekday.toLowerCase() === weekday
      );

      for (const a of weeklies) {
        for (const i of a.intervals) {
          events.push({
            id: `${i.id}-${iso}`,
            start: `${iso}T${i.from}`,
            end: `${iso}T${i.to}`,
            display: i.unavailable ? 'background' : 'block',
            color: i.unavailable ? '#ccc' : '#3788d8',
            title: `${i.from}-${i.to}`
          });
        }
      }
    }
  } else {
    // fallback: recurring events without per-date overrides
    for (const a of availabilities) {
      if (!a.date && a.weekday) {
        for (const i of a.intervals) {
          events.push({
            id: i.id,
            daysOfWeek: [weekdayToNumber(a.weekday)],
            startTime: i.from,
            endTime: i.to,
            display: i.unavailable ? 'background' : 'block',
            color: i.unavailable ? '#ccc' : '#3788d8',
            title: `${i.from}-${i.to}`
          });
        }
      }
    }
  }

  return events;
}


export interface SesamiBooking {
  id: string;
  appointmentId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  status: string;
  price: number;
  currency: string;
  duration: number;
  datetime: string;
  serviceName: string;
  variantTitle: string;
  teamMember: string;
}

export interface SesamiResource {
  id: string;
  name: string;
  email: string;
  availabilities: SesamiAvailability[];
}

export interface SesamiAvailability {
  date: string | null;
  id?: string;
  weekday: string | null;
  type: 'date' | 'wday';
  intervals: SesamiInterval[];
}

export interface SesamiInterval {
  from: string;
  id?: string;
  to: string;
  unavailable: boolean;
}

const CalendarPage: FC = () => {
  const { t } = useTranslation();
  useRedirectOnAuth([AuthState.UNAUTHORIZED], ROUTE.LOGIN);
  const styles = useStyles(CalendarStyles, {});
  const { apiClient } = useContext(AuthContext);
  const { userAttributes } = useContext(AuthContext);

  // Tab state
  const [selectedTab, setSelectedTab] = useState('bookings');

  // State for Sesami bookings
  const [sesamiBookings, setSesamiBookings] = useState<EventInput[]>([]);
  const [resource, setResource] = useState<SesamiResource | null>(null);
  const [availabilities, setAvailabilities] = useState<SesamiAvailability[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<SesamiBooking | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [visibleRange, setVisibleRange] = useState<{ start: string | null; end: string | null }>({
    start: null,
    end: null,
  });

  // Fetch Sesami bookings
  const fetchSesamiBookings = async () => {
    try {
      setError(null);
      const resources = await getSesamiResources(apiClient);
      const sesamiResource = resources.find((r) => r.email === userAttributes?.email);
      setResource(sesamiResource || null);
      setAvailabilities(removeTypename(sesamiResource?.availabilities || []));

      const bookings = await getSesamiBookings(100, sesamiResource?.id || '', apiClient); // Fetch up to 100 bookings, for doctors filter by email, for staff/admin fetch all
      setSesamiBookings(convertBookingsToEvents(bookings));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch Sesami bookings');
      console.error('Error fetching Sesami bookings:', err);
    }
  };

  // Convert Sesami bookings to calendar events
  const convertBookingsToEvents = (bookings: SesamiBooking[]): EventInput[] => {
    return bookings.map((booking) => {
      // Determine color based on status
      let color = '#3174ad'; // Default blue
      switch (booking.status) {
        case 'confirmed':
          color = '#7cb342'; // Green
          break;
        case 'pending':
          color = '#f57c00'; // Orange
          break;
        case 'cancelled':
          color = '#d32f2f'; // Red
          break;
        default:
          color = '#3174ad'; // Blue
      }

      return {
        ...booking,
        id: booking.id,
        title: `${booking.customerName} - ${booking.serviceName}`,
        start: booking.datetime,
        end: new Date(new Date(booking.datetime).getTime() + booking.duration * 60000).toISOString(),
        color: color,
      };
    });
  };

  const availabilityEvents = useMemo(() => {
    if (!visibleRange.start || !visibleRange.end) {
      // no visible range yet -> fallback to recurring events
      return mapSesamiToEvents(availabilities || []);
    }
    // pass inclusive start/end as "YYYY-MM-DD"
    return mapSesamiToEvents(availabilities || [], visibleRange.start, visibleRange.end);
  }, [availabilities, visibleRange]);

  // Get events based on selected tab
  const getCurrentEvents = () => {
    if (selectedTab === 'bookings') {
      return sesamiBookings;
    } else {
      return availabilityEvents;
    }
  };

  // Load Sesami bookings on component mount
  useEffect(() => {
    fetchSesamiBookings();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEventClick = (clickInfo: any) => {
    const event = clickInfo.event;
    // Only handle booking clicks, not availability clicks
    if (selectedTab === 'bookings') {
      const booking = sesamiBookings.find((b) => b.id === event.id) as SesamiBooking;
      setSelectedBooking(booking);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateSelect = (selectInfo: any) => {
    // Only open availability modal if we're on the availabilities tab
    if (selectedTab === 'availabilities') {
      const clickedDate = selectInfo.startStr.split('T')[0]; // Get date part only
      setSelectedDate(clickedDate);
      setIsAvailabilityModalOpen(true);
    }
  };

  const handleCreateAvailability = (availability: SesamiAvailability) => {
    setHasChanges(true);
    setAvailabilities((prev) => {
      // --- Case 1: Specific date availability ---
      if (availability.date) {
        const updated = prev.filter((a) => {
          // remove existing availability for the same date
          if (a.date === availability.date) return false;
          return true;
        });

        return [
          ...updated,
          {
            id: `date-${availability.date}`, // you may generate UUID instead
            date: availability.date,
            weekday: null,
            type: 'date',
            intervals: availability.intervals.map((i: SesamiInterval, idx: number) => ({
              id: `interval-${availability.date}-${idx}`,
              from: i.from,
              to: i.to,
              unavailable: i.unavailable ?? false,
            })),
          },
        ];
      }

      // --- Case 2: Recurring weekday availability ---
      if (availability.weekday) {
        const updated = prev.filter((a) => {
          // remove existing recurring availability for this weekday
          if (a.weekday === availability.weekday && !a.date) return false;

          // also remove exclusions (empty intervals) for this weekday,
          // since we're explicitly creating availability again
          if (a.date && a.intervals.length === 0) {
            const weekday = getWeekdayFromDate(a.date);
            if (weekday === availability.weekday) {
              return false;
            }
          }
          return true;
        });

        return [
          ...updated,
          {
            id: `weekday-${availability.weekday}`, // you may generate UUID instead
            date: null,
            weekday: availability.weekday,
            type: 'wday',
            intervals: availability.intervals.map((i: SesamiInterval, idx: number) => ({
              id: `interval-${availability.weekday}-${idx}`,
              from: i.from,
              to: i.to,
              unavailable: i.unavailable ?? false,
            })),
          },
        ];
      }

      return prev; // if neither date nor weekday provided
    });
  };

  const handleRemoveAvailability = (
    availability: SesamiAvailability,
    mode?: 'single' | 'all'
  ) => {
    setHasChanges(true);
    if (availability.date) {
      setAvailabilities((prev) => prev.filter((a) => a.id !== availability.id));
    } else if (availability.weekday) {
      if (mode === 'all') {
        setAvailabilities((prev) =>
          prev.filter((a) => {
            // Remove the recurring rule itself
            if (a.id === availability.id) return false;

            // Remove explicit exclusions (date + empty intervals) for the same weekday
            if (a.date && a.intervals.length === 0) {
              const weekday = getWeekdayFromDate(a.date);
              if (weekday === availability.weekday) {
                return false;
              }
            }

            return true; // keep everything else
          })
        );
      } else if (mode === 'single' && selectedDate) {
        setAvailabilities((prev) => [
          ...prev,
          {
            id: `excluded-${selectedDate}`,
            date: selectedDate,
            weekday: null,
            intervals: [], // empty means blocked
            type: 'wday',
          },
        ]);
      }
    }
  };
  
  const handleUpdateAvailabilities = async () => {
    try {
      setError(null);

      if (!resource?.id) {
        setError('No resource found for current user');
        return;
      }
      // Call the API to patch availabilities
      const success = await patchSesamiAvailabilities(resource.id, availabilities, apiClient);
      
      if (success) {
        await fetchSesamiBookings();
        setHasChanges(false);
      } else {
        setError('Failed to update availabilities');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update availabilities');
      console.error('Error updating availabilities:', err);
    }
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Box sx={{ width: '100%' }}>
          <Tabs
            value={selectedTab}
            onChange={(_event: React.SyntheticEvent, newValue: string) => {
              setSelectedTab(newValue);
            }}
          >
            <Tab value="bookings" label={t('Bookings')} />
            <Tab value="availabilities" label={t('Availabilities')} />
          </Tabs>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <RefreshButton onClick={fetchSesamiBookings} />
          <SaveButton disabled={!hasChanges} onClick={handleUpdateAvailabilities} />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={styles.container}>
        <Paper sx={styles.calendarPaper}>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={getCurrentEvents()}
            height="calc(100vh - 225px)"
            eventClick={handleEventClick}
            selectable={true}
            select={handleDateSelect}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek,dayGridDay',
            }}
            buttonText={{
              today: t('Today'),
              month: t('Month'),
              week: t('Week'),
              day: t('Day'),
            }}
            locale="de"
            firstDay={1} // Start week on Monday
            eventDisplay="block"
            dayMaxEvents={3}
            moreLinkClick="popover"
            datesSet={(info) => {
              // info.startStr and info.endStr are ISO datetimes; convert to date-only ISO
              const startISO = info.startStr.split('T')[0];
              // info.endStr is exclusive end; we want inclusive end -> subtract 1 day
              const endDate = new Date(info.endStr);
              endDate.setDate(endDate.getDate() - 1);
              const endISO = endDate.toISOString().split('T')[0];
              setVisibleRange({ start: startISO, end: endISO });
            }}
          />
        </Paper>

        {selectedTab === 'bookings' && (
          <Grid container spacing={2} sx={styles.legendContainer}>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={styles.legendItem}>
                <Box sx={{ ...styles.legendColor, backgroundColor: '#f57c00' }} />
                <Typography variant="body2">{t('Pending')}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={styles.legendItem}>
                <Box sx={{ ...styles.legendColor, backgroundColor: '#7cb342' }} />
                <Typography variant="body2">{t('Confirmed')}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={styles.legendItem}>
                <Box sx={{ ...styles.legendColor, backgroundColor: '#d32f2f' }} />
                <Typography variant="body2">{t('Cancelled')}</Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Booking Details Modal - only show for bookings tab */}
      {selectedTab === 'bookings' && (
        <CustomModal variant="large" isModalOpen={!!selectedBooking} onModalClose={() => setSelectedBooking(null)}>
          {selectedBooking && (
            <BookingDetails
              onModalClose={() => setSelectedBooking(null)}
              onBookingUpdate={fetchSesamiBookings}
              bookingData={selectedBooking}
            />
          )}
        </CustomModal>
      )}

      {/* Availability Modal - only show for availabilities tab */}
      {selectedTab === 'availabilities' && (
        <CustomModal
          variant="large"
          isModalOpen={isAvailabilityModalOpen}
          onModalClose={() => {
            setIsAvailabilityModalOpen(false);
            setSelectedDate(null);
          }}
        >
          <AvailabilityModal
            selectedDate={selectedDate}
            availabilities={availabilities || []}
            onClose={() => {
              setIsAvailabilityModalOpen(false);
              setSelectedDate(null);
            }}
            onRemoveAvailability={handleRemoveAvailability}
            onCreateAvailability={handleCreateAvailability}
          />
        </CustomModal>
      )}
    </Box>
  );
};

export default CalendarPage;
